import React from 'react';

/**
 * ISO27001Section5 component displaying documentation requirements for Section 5
 * @returns {JSX.Element}
 */
function ISO27001Section5() {
  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-6 text-gray-800">Section 5: Leadership</h1>
      
      <div className="bg-white rounded-xl shadow-md p-8 border border-gray-100 mb-6">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">Comprehensive Documentation Requirements</h2>
        <p className="text-gray-700 mb-6">
          This guide outlines Section 5 of ISO 27001, its requirements, and provides an exhaustive list 
          of all documents that need to be created to demonstrate compliance, with clear delineation 
          between Kolega's responsibilities and client responsibilities.
        </p>
        
        <div className="bg-blue-50 rounded-lg p-6 mb-8 border-l-4 border-blue-500">
          <h3 className="text-xl font-bold mb-3 text-gray-800">Overview of Section 5</h3>
          <p className="text-gray-700">
            Section 5 "Leadership" focuses on management's role in establishing, implementing, and 
            maintaining the Information Security Management System (ISMS). It emphasizes top management's 
            commitment, policy development, and clear assignment of roles and responsibilities.
          </p>
        </div>
        
        <h2 className="text-2xl font-bold mb-4 text-gray-800">Section Breakdown and Document Requirements</h2>
        
        <div className="mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300 mb-6">
            <h3 className="text-xl font-bold mb-3 text-gray-800">5.1 Leadership and commitment</h3>
            
            <div className="mb-4">
              <h4 className="font-bold text-gray-700 mb-2">Requirement:</h4>
              <p className="text-gray-700 mb-2">Top management shall demonstrate leadership and commitment with respect to the information security management system by:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>(a) ensuring the information security policy and objectives are established and compatible with the organization's strategic direction</li>
                <li>(b) ensuring the integration of ISMS requirements into the organization's processes</li>
                <li>(c) ensuring resources needed for the ISMS are available</li>
                <li>(d) communicating the importance of effective information security management</li>
                <li>(e) ensuring the ISMS achieves its intended outcomes</li>
                <li>(f) directing and supporting persons to contribute to ISMS effectiveness</li>
                <li>(g) promoting continual improvement</li>
                <li>(h) supporting other relevant management roles to demonstrate their leadership</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-gray-700 mb-2">Required Documents:</h4>
              
              <div className="mb-4">
                <div className="flex items-center mb-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                  <h5 className="font-bold text-blue-700">Kolega's Responsibilities:</h5>
                </div>
                <div className="ml-5 space-y-2">
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">ISMS Governance Structure</p>
                    <p className="text-gray-600 text-sm">Defining Kolega's technical security governance</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">ISMS Technical Leadership Meeting Minutes</p>
                    <p className="text-gray-600 text-sm">Documenting regular security leadership meetings</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Technical Security Roadmap</p>
                    <p className="text-gray-600 text-sm">Planning document for security improvements</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Resource Allocation Documentation</p>
                    <p className="text-gray-600 text-sm">Evidence of resources dedicated to security</p>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="flex items-center mb-2">
                  <div className="w-3 h-3 bg-amber-500 rounded-full mr-2"></div>
                  <h5 className="font-bold text-amber-700">Client Responsibilities:</h5>
                </div>
                <div className="ml-5 space-y-2">
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">ISMS Governance Council Meeting Minutes</p>
                    <p className="text-gray-600 text-sm">Quarterly meeting records (documented in Vanta)</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Management Communication Plan</p>
                    <p className="text-gray-600 text-sm">For information security awareness</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">ISMS Implementation Plan</p>
                    <p className="text-gray-600 text-sm">With project timeline and milestones</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Leadership Training Records</p>
                    <p className="text-gray-600 text-sm">Evidence of security leadership development</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300 mb-6">
            <h3 className="text-xl font-bold mb-3 text-gray-800">5.2 Policy</h3>
            
            <div className="mb-4">
              <h4 className="font-bold text-gray-700 mb-2">Requirement:</h4>
              <p className="text-gray-700 mb-2">Top management shall establish an information security policy that:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>(a) is appropriate to the purpose of the organization</li>
                <li>(b) includes information security objectives or framework for setting objectives</li>
                <li>(c) includes a commitment to satisfy applicable requirements</li>
                <li>(d) includes a commitment to continual improvement</li>
              </ul>
              <p className="text-gray-700 my-2">The information security policy shall:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>(e) be available as documented information</li>
                <li>(f) be communicated within the organization</li>
                <li>(g) be available to interested parties, as appropriate</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-gray-700 mb-2">Required Documents:</h4>
              
              <div className="mb-4">
                <div className="flex items-center mb-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                  <h5 className="font-bold text-blue-700">Kolega's Responsibilities:</h5>
                </div>
                <p className="text-gray-700 ml-5 mb-2">Based on the exhaustive list from your Vanta report and ISO 27001 responsibilities document, Kolega must develop and maintain:</p>
                <div className="ml-5 space-y-2">
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">02-ISMS Information Security Management System (ISMS) Policy</p>
                    <p className="text-gray-600 text-sm">Core technical ISMS framework</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Access Control Policy</p>
                    <p className="text-gray-600 text-sm">Technical implementation of access controls and authentication</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Asset Management Policy</p>
                    <p className="text-gray-600 text-sm">Management of technical assets and information classification</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Business Continuity and Disaster Recovery Plan</p>
                    <p className="text-gray-600 text-sm">Technical recovery mechanisms</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Cryptography Policy</p>
                    <p className="text-gray-600 text-sm">Encryption standards and key management</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Data Management Policy</p>
                    <p className="text-gray-600 text-sm">Technical data protection and lifecycle management</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Human Resource Security Policy</p>
                    <p className="text-gray-600 text-sm">Technical aspects of personnel security</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Incident Response Plan</p>
                    <p className="text-gray-600 text-sm">Technical incident management procedures</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Information Security Policy (AUP)</p>
                    <p className="text-gray-600 text-sm">Technical acceptable use guidelines</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Information Security Roles and Responsibilities</p>
                    <p className="text-gray-600 text-sm">Technical security organization</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Operations Security Policy</p>
                    <p className="text-gray-600 text-sm">Technical operations security procedures</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Physical Security Policy</p>
                    <p className="text-gray-600 text-sm">Security of Kolega's infrastructure</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Risk Management Policy</p>
                    <p className="text-gray-600 text-sm">Technical risk assessment and treatment</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Secure Development Policy</p>
                    <p className="text-gray-600 text-sm">Security in the development lifecycle</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Third-Party Management Policy</p>
                    <p className="text-gray-600 text-sm">Managing technical vendors and services</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Code of Conduct</p>
                    <p className="text-gray-600 text-sm">Expected behavior for Kolega technical staff</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">10-ISMS Information Security Objectives Plan</p>
                    <p className="text-gray-600 text-sm">Measurable technical objectives</p>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="flex items-center mb-2">
                  <div className="w-3 h-3 bg-amber-500 rounded-full mr-2"></div>
                  <h5 className="font-bold text-amber-700">Client Responsibilities:</h5>
                </div>
                <div className="ml-5 space-y-2">
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Organizational versions of all the above policies</p>
                    <p className="text-gray-600 text-sm">That reference and integrate with Kolega's technical policies</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Evidence of personnel agreement to all applicable policies</p>
                    <p className="text-gray-600 text-sm">As shown in Vanta checklist</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300 mb-6">
            <h3 className="text-xl font-bold mb-3 text-gray-800">5.3 Organizational roles, responsibilities and authorities</h3>
            
            <div className="mb-4">
              <h4 className="font-bold text-gray-700 mb-2">Requirement:</h4>
              <p className="text-gray-700 mb-2">Top management shall ensure that responsibilities and authorities for roles relevant to information security are assigned and communicated.</p>
              <p className="text-gray-700 mb-2">Top management shall assign responsibility and authority for:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>(a) ensuring the ISMS conforms to ISO 27001 requirements</li>
                <li>(b) reporting on ISMS performance to top management</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-gray-700 mb-2">Required Documents:</h4>
              
              <div className="mb-4">
                <div className="flex items-center mb-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                  <h5 className="font-bold text-blue-700">Kolega's Responsibilities:</h5>
                </div>
                <div className="ml-5 space-y-2">
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">03-ISMS Roles, Responsibilities, and Authorities</p>
                    <p className="text-gray-600 text-sm">For Kolega's organization</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Technical Security Reporting Structure</p>
                    <p className="text-gray-600 text-sm">Organizational chart for security functions</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Security Committee Charter</p>
                    <p className="text-gray-600 text-sm">Defining technical governance bodies</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Security Performance Reporting Templates</p>
                    <p className="text-gray-600 text-sm">For reporting to management</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Job Descriptions</p>
                    <p className="text-gray-600 text-sm">With security responsibilities for technical roles</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Delegation of Authority Documentation</p>
                    <p className="text-gray-600 text-sm">For security decisions</p>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="flex items-center mb-2">
                  <div className="w-3 h-3 bg-amber-500 rounded-full mr-2"></div>
                  <h5 className="font-bold text-amber-700">Client Responsibilities:</h5>
                </div>
                <div className="ml-5 space-y-2">
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">03-ISMS Roles, Responsibilities, and Authorities</p>
                    <p className="text-gray-600 text-sm">For client organization</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Organizational Security Reporting Structure</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">ISMS Performance Reporting Process</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Job Descriptions</p>
                    <p className="text-gray-600 text-sm">With security responsibilities for organizational roles</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 rounded-xl p-6 mb-8 border border-gray-200">
          <h3 className="text-xl font-bold mb-3 text-gray-800">Document Availability and Distribution Requirements</h3>
          <p className="text-gray-700 mb-3">For all policies and documents created by both Kolega and clients:</p>
          
          <div className="space-y-4">
            <div>
              <h4 className="font-bold text-gray-700 mb-2">1. Document Control Information:</h4>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Version number and change history</li>
                <li>Approval signatures and dates</li>
                <li>Review frequency and next review date</li>
                <li>Document owner and maintainer</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-gray-700 mb-2">2. Distribution Methods:</h4>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Security policies must be available to all relevant personnel</li>
                <li>Evidence of communication and accessibility</li>
                <li>Training records for policy awareness</li>
                <li>Acknowledgment records for key policies</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-gray-700 mb-2">3. Third-Party Availability:</h4>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Determine which policies should be available to third parties</li>
                <li>Establish secure sharing mechanisms</li>
                <li>Track third-party acknowledgments where required</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="bg-purple-50 rounded-xl p-6 mb-8 border-l-4 border-purple-500">
          <h3 className="text-xl font-bold mb-3 text-gray-800">Integration Between Kolega and Client Responsibilities</h3>
          <p className="text-gray-700 mb-3">To ensure seamless ISO 27001 compliance:</p>
          
          <ol className="list-decimal pl-6 text-gray-700 space-y-2">
            <li><span className="font-semibold">Document Mapping Matrix</span> - Cross-reference client and Kolega policies</li>
            <li><span className="font-semibold">Joint Governance Framework</span> - Define interfaces between governance structures</li>
            <li><span className="font-semibold">Policy Update Process</span> - Notification and update procedures across organizations</li>
            <li><span className="font-semibold">Comprehensive RACI Matrix</span> - Detailing all security responsibilities</li>
            <li><span className="font-semibold">Joint Review Schedule</span> - For regular review of integrated documentation</li>
          </ol>
        </div>
        
        <div className="bg-green-50 rounded-xl p-6 border-l-4 border-green-500">
          <h3 className="text-xl font-bold mb-3 text-gray-800">Implementation Guidance</h3>
          <p className="text-gray-700 mb-3">Based on the Vanta report and ISO 27001 requirements, the following implementation approach is recommended:</p>
          
          <div className="mb-4">
            <h4 className="font-bold text-blue-700 mb-2">For Kolega:</h4>
            <ol className="list-decimal pl-6 text-gray-700 space-y-2">
              <li>
                <span className="font-semibold">Develop the core policy framework:</span>
                <ul className="list-disc pl-6 mt-1 space-y-1">
                  <li>02-ISMS Policy</li>
                  <li>03-ISMS Roles, Responsibilities, and Authorities</li>
                  <li>Information Security Policy (AUP)</li>
                </ul>
              </li>
              <li>
                <span className="font-semibold">Create technical control policies:</span>
                <ul className="list-disc pl-6 mt-1 space-y-1">
                  <li>Access Control Policy</li>
                  <li>Cryptography Policy</li>
                  <li>Secure Development Policy</li>
                  <li>Operations Security Policy</li>
                </ul>
              </li>
              <li>
                <span className="font-semibold">Develop operational policies:</span>
                <ul className="list-disc pl-6 mt-1 space-y-1">
                  <li>Incident Response Plan</li>
                  <li>Business Continuity and Disaster Recovery Plan</li>
                  <li>Risk Management Policy</li>
                  <li>Asset Management Policy</li>
                </ul>
              </li>
              <li>
                <span className="font-semibold">Create remaining policies and supporting documentation:</span>
                <ul className="list-disc pl-6 mt-1 space-y-1">
                  <li>Human Resource Security Policy</li>
                  <li>Physical Security Policy</li>
                  <li>Third-Party Management Policy</li>
                  <li>Data Management Policy</li>
                  <li>Code of Conduct</li>
                </ul>
              </li>
              <li>
                <span className="font-semibold">Establish governance and reporting mechanisms:</span>
                <ul className="list-disc pl-6 mt-1 space-y-1">
                  <li>ISMS Governance Structure</li>
                  <li>Security Committee Charter</li>
                  <li>Performance Reporting Templates</li>
                </ul>
              </li>
            </ol>
          </div>
          
          <div>
            <h4 className="font-bold text-amber-700 mb-2">For Clients:</h4>
            <ol className="list-decimal pl-6 text-gray-700 space-y-2">
              <li>
                <span className="font-semibold">Develop foundational documents with reference to Kolega policies:</span>
                <ul className="list-disc pl-6 mt-1 space-y-1">
                  <li>02-ISMS Policy</li>
                  <li>03-ISMS Roles, Responsibilities, and Authorities</li>
                  <li>10-ISMS Information Security Objectives Plan</li>
                </ul>
              </li>
              <li>Create organizational policies that reference Kolega's technical controls</li>
              <li>Establish governance mechanisms that interface with Kolega</li>
              <li>Implement policy acknowledgment and training processes</li>
              <li>Maintain ISMS Governance Council Meeting Minutes</li>
            </ol>
          </div>
          
          <p className="text-gray-700 mt-4">
            This comprehensive approach ensures all required documentation from the Vanta checklist is created and maintained, 
            with clear responsibilities between Kolega and clients.
          </p>
        </div>
      </div>
    </div>
  );
}

export default ISO27001Section5;
